import type {ReactElement, ReactNode} from 'react'
import {Link} from '@primer/react'
import {GitPullRequestDraftIcon} from '@primer/octicons-react'
import {MergeabilityFailureIcon} from '@github-ui/custom-icons/MergeabilityFailureIcon'
import {ReviewApprovedIcon} from '@github-ui/custom-icons/ReviewApprovedIcon'
import {isConversationResolutionRequired} from '@github-ui/mergebox/helpers/conversation-resolution'
import {isFailingOrIncompleteStatus, isPendingStatus} from '@github-ui/mergebox/page-data/payloads/status-checks'
import type {FailingRulesAndConditionPayload, RuleRollupPayload} from '@github-ui/mergebox/types'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {useLayoutQuery} from '../../../../page-data/loaders/use-layout-query'
import {useThreadCountsQuery} from '../../../../page-data/loaders/use-tab-counts'
import {usePendingReviews} from '../../../../stores/layout-selectors'
import {PullRequestState} from '../../../../types'
import type {ForYouItemVisual} from '../shared/for-you-item.types'
import {useViewerIsReviewer} from '../shared/use-viewer-is-reviewer'
import {useViewerRoles} from '../shared/use-viewer-roles'
import {useDownstackPendingReviewRequests} from '../stack/use-downstack-pending-review-request'
import {hasUnhandledMergeBlocker} from './merge-blocked-visibility'
import {useMergeabilityData, type MergeabilityData} from './use-mergeability-data'

export type SummaryPullRequest = {number: number; url: string}
export type SummaryKind = 'draft' | 'blocked' | 'pending' | 'ready'
/** Reasons merging is blocked, named as they appear in the "Merge blocked by …" title. */
export type BlockingReason =
  | 'merge conflicts'
  | 'previous PRs'
  | 'checks'
  | 'workflow approval'
  | 'reviews'
  | 'comments'
  | 'branch update'
  | 'merge rules'
/**
 * Who is viewing the pull request.
 * - `author`: the author or an assignee; repair wording.
 * - `reviewer`: requested, has reviewed, has a review in progress, or has push access; review-oriented wording.
 * - `observer`: anyone else; subscription wording.
 */
export type SummaryAudience = 'author' | 'reviewer' | 'observer'

export type CombinedSummary = {
  kind: SummaryKind
  title: string
  /** Replaces the item's description, or follows it when `keepItemDescription` is set. */
  sentence: ReactNode[]
  keepItemDescription: boolean
}

export type RenderedCombinedSummary = {
  title: string
  description: ReactNode
  visual: ForYouItemVisual
}

type MergeRequirements = NonNullable<MergeabilityData['mergeRequirements']>

/** Everything the summary needs to know about the pull request, derived once from mergeability data. */
type SummaryFacts = {
  /** Earlier pull requests in the stack that block this one, deduplicated by URL, from the base of the stack up. */
  blockedPreviousPullRequests: SummaryPullRequest[]
  hasMergeConflicts: boolean
  /** The branch is behind and branch protection requires updating it before merging. */
  branchUpdateRequired: boolean
  /** The branch can be updated, but merging does not require it. */
  branchUpdateOptional: boolean
  /** Some other branch in the stack is behind its base. */
  stackBranchesOutOfDate: boolean
  /** Shared sentence about the branch, " Updating the branch is optional.", or empty when nothing applies. */
  branchStatusNote: string
  /** Workflow runs are waiting for a maintainer to approve them before they can run. */
  workflowsAwaitingApproval: boolean
  failingRequiredCheckCount: number
  pendingRequiredCheckCount: number
  /** Optional checks that failed or are still running. */
  optionalChecksNeedingAttentionCount: number
  /** A review requesting changes is blocking the merge. */
  requestedChangesBlockMerge: boolean
  /** More approving reviews are required before merging. */
  missingRequiredApprovals: boolean
  /** Unresolved comments must be resolved before merging. */
  unresolvedCommentsBlockMerge: boolean
  /** Unresolved comments that do not block merging. */
  optionalUnresolvedCommentCount: number
  /** Optional checks, unresolved comments and non-blocking requested changes that may need attention. */
  optionalItemsNeedingAttentionCount: number
  /** Requested reviews that have not been submitted yet; they do not block merging. */
  hasPendingReviewRequests: boolean
  /** A failing rule or condition the summary has no specific wording for blocks the merge. */
  blockedByUnhandledPolicy: boolean
  /** Distinct reasons merging is blocked, in the For You deciders' priority order. */
  blockingReasons: BlockingReason[]
  /** The merge is blocked, but none of the known reasons explain it. */
  blockedForUnknownReason: boolean
}

const BLOCKED_PREVIOUS_STATUSES = new Set(['NOT_READY', 'DRAFT', 'CLOSED', 'MERGE_CONFLICTS', 'NEEDS_REBASE'])
const CURRENT_STACK_REPAIR_STATUSES = new Set(['MERGE_CONFLICTS', 'NEEDS_REBASE'])
const MAX_LINKED_PREVIOUS_PULL_REQUESTS = 2
const UNKNOWN_STACK_STATUSES = new Set(['UNKNOWN', 'PENDING', 'LOADING_MERGE_STATE'])
// Matches the merge box's blocked section: merge queue and status check rules, the repository and pull request state,
// and stacks have their own presentation, so they are not reported as failing merge rules.
const RULE_TYPES_EXCLUDED_FROM_MERGE_RULES = new Set(['MERGE_QUEUE', 'REQUIRED_STATUS_CHECKS'])
const CONDITION_TYPES_EXCLUDED_FROM_MERGE_RULES = new Set(['PULL_REQUEST_REPO_STATE', 'PULL_REQUEST_STATE', 'STACK'])
// Both describe whether the viewer may push, as the merge box's `isUserBlockedFromPushing` does for the rule.
const VIEWER_AUTHORIZATION_RULE_TYPE = 'AUTHORIZATION'
const VIEWER_CANNOT_PUSH_FAILURE = 'USER_CANNOT_PUSH'
const APPROVAL_FAILURES = new Set([
  'MORE_REVIEWS_REQUIRED',
  'CODE_OWNER_REVIEW_REQUIRED',
  'REQUIRED_REVIEWER_APPROVAL_REQUIRED',
  'SOC2_APPROVAL_PROCESS_REQUIRED',
  'LAST_PUSH_APPROVAL_REQUIRED',
])

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Summarizes every merge blocker and advisory for an open or draft pull request as the selected item's title,
 * description and status visual. Returns null when the item should describe itself.
 */
export function useCombinedSummary(itemDescription: ReactNode): RenderedCombinedSummary | null {
  const data = useMergeabilityData()
  const unresolvedComments = useThreadCountsQuery().data?.unresolvedThreads ?? 0
  const audience = useSummaryAudience(data?.mergeSection.canUserPushToBase ?? false)
  const {pullRequest, stack} = useLayoutQuery().data
  const {data: previousPullRequestsAwaitingReview} = useDownstackPendingReviewRequests(stack, pullRequest.number)
  const state = usePullRequestState()
  if (state !== PullRequestState.Open && state !== PullRequestState.Draft) return null

  const summary = decideCombinedSummary({
    data,
    unresolvedComments,
    audience,
    hasItemDescription: Boolean(itemDescription),
    reviewRequestedOnPreviousPullRequests: previousPullRequestsAwaitingReview.length > 0,
  })
  if (!summary) return null
  return {
    title: summary.title,
    description: summary.keepItemDescription ? (
      <>
        {itemDescription}
        {summary.sentence}
      </>
    ) : (
      summary.sentence
    ),
    visual: summaryVisual(summary.kind),
  }
}

function useSummaryAudience(canUserPushToBase: boolean): SummaryAudience {
  const roles = useViewerRoles()
  // Requested or completed reviews, including on drafts, plus a review the viewer has in progress.
  const isReviewer = useViewerIsReviewer()
  const pendingReview = usePendingReviews()
  if (roles.isAuthor || roles.isAssignee) return 'author'
  return isReviewer || pendingReview?.id || canUserPushToBase ? 'reviewer' : 'observer'
}

function summaryVisual(kind: SummaryKind): ForYouItemVisual {
  // Matches the draft card's own icon.
  if (kind === 'draft') return {kind: 'icon', icon: GitPullRequestDraftIcon, tone: 'neutral', glyphSize: 12}
  if (kind === 'pending') return {kind: 'spinner', tone: 'attention'}
  if (kind === 'blocked') return {kind: 'icon', icon: MergeabilityFailureIcon, tone: 'danger', glyphSize: 16}
  return {kind: 'icon', icon: ReviewApprovedIcon, tone: 'success', glyphSize: 16}
}

// ---------------------------------------------------------------------------
// Decision
// ---------------------------------------------------------------------------

export function decideCombinedSummary({
  data,
  unresolvedComments,
  audience,
  hasItemDescription = true,
  reviewRequestedOnPreviousPullRequests = false,
}: {
  data: MergeabilityData | null
  unresolvedComments: number
  audience: SummaryAudience
  /** Whether the selected card has a description that could be kept. */
  hasItemDescription?: boolean
  /** The viewer is asked to review earlier pull requests in the stack. */
  reviewRequestedOnPreviousPullRequests?: boolean
}): CombinedSummary | null {
  // 1. Closed, merged and queued items describe themselves.
  if (!data || data.isClosedOrMerged || data.mergeBoxPullRequest.isInMergeQueue) return null

  // 2. Never claim readiness without knowing it; the selected item describes itself instead.
  if (!data.mergeRequirements || isMergeStatusUnknown(data, data.mergeRequirements)) return null

  // 3. Gather the facts once.
  const isDraft = data.mergeBoxPullRequest.isDraft
  const facts = collectSummaryFacts(data, data.mergeRequirements, unresolvedComments, isDraft)
  // An unrecognized unmergeable result must not become a ready summary.
  if (facts.blockedForUnknownReason) return null

  // 4. Drafts are summarized against becoming ready for review rather than merging.
  if (isDraft) return summarizeDraft(facts, audience)

  // 5. Classify the state and title it.
  const kind = classifySummary(facts)
  const title = summaryTitle(facts.blockingReasons)

  // 6. Keep the selected card's description while it still describes the pull request accurately.
  if (hasItemDescription && keepsItemDescription(facts, audience, reviewRequestedOnPreviousPullRequests)) {
    return {kind, title, sentence: [facts.branchStatusNote], keepItemDescription: true}
  }

  // 7. Otherwise write the sentence for the viewer.
  return {kind, title, sentence: summarySentence(facts, audience, kind), keepItemDescription: false}
}

/**
 * Review requirements only apply once a draft is ready for review, so they are left out. The author is only told the
 * draft is "Not ready for review" when something else blocks it. Only the author can mark it ready, so everyone else
 * gets their usual subtitle for anything else that blocks it, or is asked to wait for it to be marked ready.
 */
function summarizeDraft(facts: SummaryFacts, audience: SummaryAudience): CombinedSummary {
  const draftFacts = {
    ...facts,
    blockingReasons: facts.blockingReasons.filter(reason => !isReviewReason(reason)),
    hasPendingReviewRequests: false,
  }
  const blocked = draftFacts.blockingReasons.length > 0
  if (audience !== 'author') {
    return {
      kind: 'draft',
      title: 'Not ready for review',
      sentence: blocked
        ? summarySentence(draftFacts, audience, 'draft')
        : ['Wait for this pull request to be marked ready for review.'],
      keepItemDescription: false,
    }
  }
  return {
    kind: 'draft',
    title: blocked ? 'Not ready for review' : 'Ready for review',
    sentence: summarySentence(draftFacts, audience, 'draft'),
    keepItemDescription: false,
  }
}

function isReviewReason(reason: BlockingReason): boolean {
  return reason === 'reviews' || reason === 'comments'
}

function classifySummary(facts: SummaryFacts): SummaryKind {
  if (facts.blockingReasons.length === 0) return 'ready'
  const onlyWaiting = facts.blockingReasons.every(
    reason => reason === 'workflow approval' || (reason === 'checks' && facts.failingRequiredCheckCount === 0),
  )
  return onlyWaiting ? 'pending' : 'blocked'
}

/** Names one or two blocking reasons, then counts the rest. */
function summaryTitle(reasons: BlockingReason[]): string {
  const [first, second] = reasons
  if (!first) return 'Ready to merge'
  if (!second) return `Merge blocked by ${first}`
  if (reasons.length === 2) return `Merge blocked by ${first} and ${second}`
  return `Merge blocked by ${first} and ${reasons.length - 1} other issues`
}

function keepsItemDescription(
  facts: SummaryFacts,
  audience: SummaryAudience,
  reviewRequestedOnPreviousPullRequests: boolean,
): boolean {
  const [reason, ...others] = facts.blockingReasons
  // The Author decider shows a single blocker's own card, which already explains that blocker specifically. The merge
  // rules catch-all card has no description, and the written sentence for previous PRs links every one of them, so
  // both get the written sentence instead.
  if (audience === 'author') {
    return reason !== undefined && others.length === 0 && reason !== 'merge rules' && reason !== 'previous PRs'
  }
  // Reviewer cards describe the viewer's own review, which stays accurate unless a non-review requirement blocks.
  // A reviewer requested on earlier stacked pull requests is asked to review those first, which covers them.
  return facts.blockingReasons.every(
    blocker => isReviewReason(blocker) || (blocker === 'previous PRs' && reviewRequestedOnPreviousPullRequests),
  )
}

function isMergeStatusUnknown(data: MergeabilityData, mergeRequirements: MergeRequirements): boolean {
  if (
    mergeRequirements.state === 'UNKNOWN' ||
    mergeRequirements.conditions.some(
      condition => condition.type !== 'PULL_REQUEST_USER_STATE' && condition.result === 'UNKNOWN',
    ) ||
    data.mergeBoxPullRequest.mergeStateStatus === 'UNKNOWN' ||
    data.statusChecks.some(check => check.isRequired && check.state === '_UNKNOWN_VALUE')
  ) {
    return true
  }

  const {stackSection} = data
  if (!stackSection.isStacked) return false
  return (
    UNKNOWN_STACK_STATUSES.has(stackSection.sectionStatus) ||
    (stackSection.stackEntries ?? []).some(
      entry => entry.position !== 'AFTER' && (!entry.effectiveEntryStatus || entry.effectiveEntryStatus === 'LOADING'),
    )
  )
}

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

function collectSummaryFacts(
  data: MergeabilityData,
  mergeRequirements: MergeRequirements,
  unresolvedComments: number,
  isDraft: boolean,
): SummaryFacts {
  const {mergeBoxPullRequest: pull, stackSection, reviewerSection, statusChecks, statusRollup} = data

  const blockedPreviousPullRequests = findBlockedPreviousPullRequests(data)
  const hasMergeConflicts = mergeRequirements.conditions.some(
    condition => condition.type === 'PULL_REQUEST_MERGE_CONFLICT_STATE' && condition.result === 'FAILED',
  )
  const branchUpdateRequired = data.mergeSection.requiresBranchUpdate
  const branchUpdateOptional = !branchUpdateRequired && !hasMergeConflicts && pull.viewerCanUpdateBranch
  const stackBranchesOutOfDate = stackSection.isStacked && pull.anyStackBranchOutdated
  // TODO: Say "The branch is up to date." once the merge box payload reports whether the branch is behind its base
  // independently of the viewer. "No conflicts" does not prove it, and `viewerCanUpdateBranch` is false for viewers
  // without permission even when the branch is behind.
  const branchStatusNote = branchUpdateOptional ? ' Updating the branch is optional.' : ''

  const approvalPending = statusRollup.combinedState === 'PENDING_APPROVAL'
  const {
    failingRequiredCheckCount,
    pendingRequiredCheckCount,
    workflowsAwaitingApproval,
    optionalChecksNeedingAttentionCount,
  } = countChecks(statusChecks, approvalPending)

  const failureReasons = reviewerSection.consolidatedFailureReasons
  const requestedChangesBlockMerge = failureReasons.includes('CHANGES_REQUESTED')
  const missingRequiredApprovals = failureReasons.some(reason => APPROVAL_FAILURES.has(reason))
  const unresolvedCommentsBlockMerge = unresolvedComments > 0 && isConversationResolutionRequired(mergeRequirements)
  const optionalUnresolvedCommentCount = unresolvedCommentsBlockMerge ? 0 : unresolvedComments
  const optionalRequestedChanges =
    !requestedChangesBlockMerge &&
    optionalUnresolvedCommentCount === 0 &&
    pull.latestOpinionatedReviews.some(review => review.state === 'CHANGES_REQUESTED')
  // Workflows can await approval before any check row exists; the rollup is then the only sign, and it is advisory.
  const optionalApprovalOnlyInRollup =
    approvalPending &&
    !workflowsAwaitingApproval &&
    !!statusRollup.pendingWorkflowApprovalRollup &&
    !statusChecks.some(check => check.state === 'ACTION_REQUIRED')
  const optionalItemsNeedingAttentionCount =
    optionalChecksNeedingAttentionCount +
    optionalUnresolvedCommentCount +
    Number(optionalRequestedChanges) +
    Number(optionalApprovalOnlyInRollup)
  const hasPendingReviewRequests = reviewerSection.pendingRequestedReviews.length > 0

  const blockedByUnhandledPolicy = hasPolicyBlocker(mergeRequirements)
  const blockedByPreviousPullRequests = blockedPreviousPullRequests.length > 0
  const previousPullRequestClosed = (stackSection.stackEntries ?? []).some(
    entry => entry.position === 'BEFORE' && entry.effectiveEntryStatus === 'CLOSED',
  )
  // Listed in the For You deciders' priority order, so titles and sentences name blockers in the order the cards
  // would. Failed and pending checks are one reason, as are requested changes and missing approvals; each takes the
  // position of its highest-priority card.
  const candidateReasons: Array<[BlockingReason, boolean]> = [
    ['merge conflicts', hasMergeConflicts],
    ['previous PRs', blockedByPreviousPullRequests && previousPullRequestClosed],
    ['checks', failingRequiredCheckCount > 0],
    ['workflow approval', workflowsAwaitingApproval],
    ['reviews', requestedChangesBlockMerge],
    ['comments', unresolvedCommentsBlockMerge],
    ['reviews', missingRequiredApprovals],
    ['branch update', branchUpdateRequired],
    ['previous PRs', blockedByPreviousPullRequests],
    ['merge rules', blockedByUnhandledPolicy],
    ['checks', pendingRequiredCheckCount > 0],
  ]
  const blockingReasons = [...new Set(candidateReasons.filter(([, applies]) => applies).map(([reason]) => reason))]
  // This pull request's own stack entry can need conflicts resolved or a rebase without the conflict or branch update
  // requirements reporting it, so the summary has no wording for that repair.
  const currentStackRepairUnexplained =
    !hasMergeConflicts &&
    !branchUpdateRequired &&
    (stackSection.stackEntries ?? []).some(
      entry => entry.position === 'CURRENT' && CURRENT_STACK_REPAIR_STATUSES.has(entry.effectiveEntryStatus ?? ''),
    )

  return {
    blockedPreviousPullRequests,
    hasMergeConflicts,
    branchUpdateRequired,
    branchUpdateOptional,
    stackBranchesOutOfDate,
    branchStatusNote,
    workflowsAwaitingApproval,
    failingRequiredCheckCount,
    pendingRequiredCheckCount,
    optionalChecksNeedingAttentionCount,
    requestedChangesBlockMerge,
    missingRequiredApprovals,
    unresolvedCommentsBlockMerge,
    optionalUnresolvedCommentCount,
    optionalItemsNeedingAttentionCount,
    hasPendingReviewRequests,
    blockedByUnhandledPolicy,
    blockingReasons,
    // Drafts can never merge, so an unmergeable draft is expected rather than unexplained.
    blockedForUnknownReason:
      currentStackRepairUnexplained ||
      (!isDraft &&
        blockingReasons.length === 0 &&
        mergeRequirements.state === 'UNMERGEABLE' &&
        !onlyViewerPermissionFailed(mergeRequirements)),
  }
}

/** Counts the checks that block or may need attention in a single pass over a potentially long list. */
function countChecks(statusChecks: MergeabilityData['statusChecks'], approvalPending: boolean) {
  let failingRequiredCheckCount = 0
  let pendingRequiredCheckCount = 0
  let unstartedRequiredCheckCount = 0
  let optionalChecksNeedingAttentionCount = 0
  for (const {isRequired, state} of statusChecks) {
    // Matches the checks progress, which treats a requested check as one that has not finished.
    const pending = isPendingStatus(state) || state === 'REQUESTED'
    const failing = !pending && isFailingOrIncompleteStatus(state)
    if (!isRequired) {
      if (pending || failing) optionalChecksNeedingAttentionCount++
    } else if (pending) {
      pendingRequiredCheckCount++
      if (state === 'EXPECTED') unstartedRequiredCheckCount++
    } else if (failing) {
      failingRequiredCheckCount++
    }
  }
  // A required check from a workflow awaiting approval has not reported yet. Approval for optional workflows alone does
  // not block merging, and checks that are already running are not waiting on it.
  const workflowsAwaitingApproval = approvalPending && unstartedRequiredCheckCount > 0
  return {
    failingRequiredCheckCount,
    // Only checks that are not waiting on approval.
    pendingRequiredCheckCount: workflowsAwaitingApproval
      ? pendingRequiredCheckCount - unstartedRequiredCheckCount
      : pendingRequiredCheckCount,
    workflowsAwaitingApproval,
    optionalChecksNeedingAttentionCount,
  }
}

/** Whether a failing rule or condition is not explained by the review, checks, conflicts or stack areas. */
function hasPolicyBlocker(mergeRequirements: MergeRequirements): boolean {
  const failingItems: FailingRulesAndConditionPayload[] = mergeRequirements.conditions.flatMap(condition => {
    if (condition.result !== 'FAILED') return []
    if (condition.type === 'PULL_REQUEST_RULES') {
      return condition.ruleRollups.filter(
        rule =>
          rule.result === 'FAILED' &&
          !RULE_TYPES_EXCLUDED_FROM_MERGE_RULES.has(rule.ruleType) &&
          rule.ruleType !== VIEWER_AUTHORIZATION_RULE_TYPE,
      )
    }
    if (condition.type === 'PULL_REQUEST_USER_STATE') {
      return condition.failedSubConditions.filter(item => item.displayName !== VIEWER_CANNOT_PUSH_FAILURE)
    }
    return CONDITION_TYPES_EXCLUDED_FROM_MERGE_RULES.has(condition.type) ? [] : [condition]
  })
  const isRule = (item: FailingRulesAndConditionPayload): item is RuleRollupPayload => 'ruleType' in item

  return hasUnhandledMergeBlocker(
    failingItems.filter(item => {
      if (isRule(item) && item.ruleType === 'PULL_REQUEST' && item.metadata && 'failureReasons' in item.metadata) {
        const reasons = item.metadata.failureReasons
        return !(
          reasons.length > 0 &&
          reasons.every(
            reason =>
              APPROVAL_FAILURES.has(reason.toUpperCase()) ||
              reason.toUpperCase() === 'CHANGES_REQUESTED' ||
              reason.toUpperCase() === 'THREAD_RESOLUTION_REQUIRED',
          )
        )
      }
      return true
    }),
  )
}

// The viewer's permission to push is not a requirement on the PR; someone with permission can still merge it.
function onlyViewerPermissionFailed(mergeRequirements: MergeRequirements): boolean {
  const failedConditions = mergeRequirements.conditions.filter(condition => condition.result === 'FAILED')
  return (
    failedConditions.length > 0 &&
    failedConditions.every(condition => {
      if (condition.type === 'PULL_REQUEST_USER_STATE') {
        return (
          condition.failedSubConditions.length > 0 &&
          condition.failedSubConditions.every(subcondition => subcondition.displayName === VIEWER_CANNOT_PUSH_FAILURE)
        )
      }
      if (condition.type === 'PULL_REQUEST_RULES') {
        const failedRules = condition.ruleRollups.filter(rule => rule.result === 'FAILED')
        return failedRules.length > 0 && failedRules.every(rule => rule.ruleType === VIEWER_AUTHORIZATION_RULE_TYPE)
      }
      return false
    })
  )
}

// Ordered from the base of the stack up, so the pull requests to fix first are named first.
function findBlockedPreviousPullRequests(data: MergeabilityData): SummaryPullRequest[] {
  const seen = new Set<string>()
  return (data.stackSection.stackEntries ?? [])
    .filter(entry => entry.position === 'BEFORE' && BLOCKED_PREVIOUS_STATUSES.has(entry.effectiveEntryStatus ?? ''))
    .flatMap(({pull}) => {
      if (seen.has(pull.url)) return []
      seen.add(pull.url)
      return [{number: pull.number, url: pull.url}]
    })
    .reverse()
}

// ---------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------

function summarySentence(facts: SummaryFacts, audience: SummaryAudience, kind: SummaryKind): ReactNode[] {
  const blocked = facts.blockingReasons.length > 0
  if (audience === 'observer') {
    return [
      blocked
        ? 'Subscribe to get notified when this PR is unblocked.'
        : 'Subscribe to get new activity, including when this PR is merged.',
    ]
  }
  return blocked ? blockingSentence(facts, audience, kind) : [nonBlockingSentence(facts, kind === 'draft')]
}

// Non-blocking summaries (#27334)

/**
 * Marking a draft ready can surface new requirements, so a draft invites the author to mark it ready rather than
 * claiming everything is resolved, followed by the same optional advice as an open pull request.
 */
function nonBlockingSentence(facts: SummaryFacts, isDraft: boolean): string {
  const items = facts.optionalItemsNeedingAttentionCount
  const someItems = items === 1 ? 'an item' : 'some items'
  const sentence: string[] = isDraft ? ['Mark this pull request as ready for review.'] : []
  if (facts.branchUpdateOptional && items > 0) {
    sentence.push(`But the branch is out of date and ${someItems} below may need attention.`)
  } else if (items > 0) {
    sentence.push(`But ${someItems} below may need attention.`)
  } else if (facts.branchUpdateOptional) {
    sentence.push('Updating the branch is optional.')
  }

  if (facts.hasPendingReviewRequests) {
    sentence.push('Reviews are still pending.')
  }
  if (facts.stackBranchesOutOfDate) {
    sentence.push('Some branches in the stack are out of date.')
  }

  // The only branch note is an optional update, which these sentences already describe.
  return sentence.length === 0 ? 'All issues are resolved.' : sentence.join(' ')
}

// Blocking summaries (#27333)

function blockingSentence(facts: SummaryFacts, audience: SummaryAudience, kind: SummaryKind): ReactNode[] {
  const steps = sentenceSteps(facts)
  const sentence = audience === 'reviewer' ? reviewerSentence(steps) : authorSentence(steps, kind === 'draft')
  return [...sentence, facts.branchStatusNote]
}

type SentenceStep = {verb: 'resolve' | 'wait' | 'update'; object: ReactNode[]}

/** What each blocking reason asks of the viewer, in the same order as the title's reasons. */
function sentenceSteps(facts: SummaryFacts): SentenceStep[] {
  const issuesBelow: SentenceStep = {verb: 'resolve', object: ['the issues below']}
  const pendingChecks =
    facts.pendingRequiredCheckCount === 1 ? 'the required check to finish' : 'the required checks to finish'

  const steps = facts.blockingReasons.flatMap((reason): SentenceStep[] => {
    switch (reason) {
      case 'merge conflicts':
        return [{verb: 'resolve', object: ['merge conflicts']}]
      case 'previous PRs':
        return [
          {
            verb: 'resolve',
            object: ['the issues in ', ...previousPullRequestLinks(facts.blockedPreviousPullRequests)],
          },
        ]
      case 'checks':
        return [
          ...(facts.failingRequiredCheckCount > 0 ? [issuesBelow] : []),
          ...(facts.pendingRequiredCheckCount > 0 ? [{verb: 'wait' as const, object: [pendingChecks]}] : []),
        ]
      case 'workflow approval':
        return [{verb: 'wait', object: ['the workflows to run']}]
      case 'branch update':
        return [{verb: 'update', object: []}]
      case 'reviews':
      case 'comments':
        return [issuesBelow]
      // Merge rules appear in the merge box rather than in the rows below the card.
      case 'merge rules':
        return [{verb: 'resolve', object: ['the failing merge rules']}]
    }
  })
  // "The issues below" covers checks, reviews and comments together, so it is named once.
  return steps.filter((step, index) => step !== issuesBelow || steps.indexOf(issuesBelow) === index)
}

/** Merges neighbouring steps with the same verb, e.g. "resolve merge conflicts and the issues below". */
function groupSteps(steps: SentenceStep[]): Array<{verb: SentenceStep['verb']; objects: ReactNode[][]}> {
  const groups: Array<{verb: SentenceStep['verb']; objects: ReactNode[][]}> = []
  for (const {verb, object} of steps) {
    const previous = groups.at(-1)
    if (previous?.verb === verb) previous.objects.push(object)
    else groups.push({verb, objects: [object]})
  }
  return groups
}

/** The same repairs apply to a draft, but they come before marking it ready rather than before merging. */
function authorSentence(steps: SentenceStep[], isDraft: boolean): ReactNode[] {
  const clauses = groupSteps(steps).map(({verb, objects}, index): ReactNode[] => {
    const lead = verb === 'update' ? 'update your branch' : verb === 'resolve' ? 'resolve ' : 'wait for '
    const capitalizedLead = index === 0 ? lead.charAt(0).toUpperCase() + lead.slice(1) : lead
    return verb === 'update' ? [capitalizedLead] : [capitalizedLead, ...joinParts(objects)]
  })
  return [...joinParts(clauses), isDraft ? ' before marking ready.' : ' before merging.']
}

function reviewerSentence(steps: SentenceStep[]): ReactNode[] {
  const waits = groupSteps(steps).map(({verb, objects}): ReactNode[] => {
    if (verb === 'update') return ['the branch to be updated']
    return verb === 'resolve' ? [...joinParts(objects), ' to be resolved'] : joinParts(objects)
  })
  return ['Wait for ', ...joinParts(waits), ' before reviewing.']
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

// Stacks can hold many pull requests, so only the first few are linked and the rest are counted.
function previousPullRequestLinks(pullRequests: SummaryPullRequest[]): ReactNode[] {
  const linked = pullRequests.slice(0, MAX_LINKED_PREVIOUS_PULL_REQUESTS)
  const others = pullRequests.length - linked.length
  const parts: ReactNode[][] = linked.map(pullRequest => [pullRequestLink(pullRequest)])
  if (others > 0) parts.push([`${others} other ${others === 1 ? 'PR' : 'PRs'}`])
  return joinParts(parts)
}

// Previous pull requests are deduplicated by URL, so it is a stable key within the sentence.
function pullRequestLink({number, url}: SummaryPullRequest): ReactElement {
  return (
    <Link key={url} inline href={url} className="Link--inTextBlock" aria-label={`Pull request #${number}`}>
      #{number}
    </Link>
  )
}

function joinParts(clauses: ReactNode[][]): ReactNode[] {
  return clauses.flatMap((clause, index) => {
    if (index === 0) return clause
    const separator = index === clauses.length - 1 ? (clauses.length > 2 ? ', and ' : ' and ') : ', '
    return [separator, ...clause]
  })
}
